import type { LaserPointer, Rect } from "@/models";

export type ZoomRect = Rect;

export const FULL_PAGE_ZOOM: ZoomRect = {
  top: 0,
  left: 0,
  bottom: 1,
  right: 1,
};

// Sentinel laserpointer meaning "hidden" — matches the iOS convention
// (ProjectorCast.LaserPointer.isHidden) written to Realtime DB at
// /presentations/{id}/laserpointer.
export const LASER_HIDDEN: LaserPointer = { x: -1, y: -1 };

export const ZOOM_EPSILON = 1e-4;

export function rectsAreClose(
  a: ZoomRect | null | undefined,
  b: ZoomRect | null | undefined,
): boolean {
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < ZOOM_EPSILON &&
    Math.abs(a.left - b.left) < ZOOM_EPSILON &&
    Math.abs(a.bottom - b.bottom) < ZOOM_EPSILON &&
    Math.abs(a.right - b.right) < ZOOM_EPSILON
  );
}

export interface ZoomViewport {
  width: number;
  height: number;
  /** PDF.js page viewBox: [xMin, yMin, xMax, yMax] in PDF user space. */
  viewBox: readonly [number, number, number, number];
  convertToPdfPoint: (x: number, y: number) => readonly [number, number];
}

interface ZoomBoundingRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Pure geometry helper for [PdfJsPresenter.vue]'s `computeVisibleZoomRect`.
 * Maps the container's currently visible part of the page DIV through the
 * viewport's own `convertToPdfPoint` and normalizes against the viewBox.
 * Rotation 0/90/180/270 are handled uniformly because PDF.js' transform
 * already encodes the rotation.
 */
export function visibleRectFromViewport(
  pageRect: ZoomBoundingRect,
  containerRect: ZoomBoundingRect,
  viewport: ZoomViewport,
): ZoomRect | null {
  if (pageRect.width <= 0 || pageRect.height <= 0) return null;
  const leftDiv = Math.max(0, containerRect.left - pageRect.left);
  const topDiv = Math.max(0, containerRect.top - pageRect.top);
  const rightDiv = Math.min(
    viewport.width,
    containerRect.right - pageRect.left,
  );
  const bottomDiv = Math.min(
    viewport.height,
    containerRect.bottom - pageRect.top,
  );
  if (rightDiv <= leftDiv || bottomDiv <= topDiv) return null;
  const pdfTL = viewport.convertToPdfPoint(leftDiv, topDiv);
  const pdfBR = viewport.convertToPdfPoint(rightDiv, bottomDiv);
  // viewBox is [xMin, yMin, xMax, yMax]; normalize against its true extent so
  // PDFs with a non-zero crop/media-box origin map correctly.
  const [vbX0, vbY0, vbX1, vbY1] = viewport.viewBox;
  const vbW = vbX1 - vbX0;
  const vbH = vbY1 - vbY0;
  if (vbW <= 0 || vbH <= 0) return null;
  const minX = Math.min(pdfTL[0], pdfBR[0]);
  const maxX = Math.max(pdfTL[0], pdfBR[0]);
  const minY = Math.min(pdfTL[1], pdfBR[1]);
  const maxY = Math.max(pdfTL[1], pdfBR[1]);
  return {
    left: (minX - vbX0) / vbW,
    right: (maxX - vbX0) / vbW,
    // PDF Y is bottom-up; the wire rect is top-down.
    top: 1 - (maxY - vbY0) / vbH,
    bottom: 1 - (minY - vbY0) / vbH,
  };
}

/** PDF.js `viewport.rawDims`: viewBox extent and origin in PDF user space. */
export interface PdfRawDims {
  pageWidth: number;
  pageHeight: number;
  pageX: number;
  pageY: number;
}

/**
 * Receiver-side inverse of [visibleRectFromViewport]: maps a normalized wire
 * zoom rect (top-down, [0,1] against the page's viewBox) to a PDF.js `FitR`
 * destination rectangle in *absolute* PDF user space — `[left, bottom, right,
 * top]` with `bottom < top`. The viewBox origin (`pageX`/`pageY`) is added back
 * so PDFs whose crop/media box does not start at (0,0) align correctly; FitR
 * and `convertToViewportPoint` both interpret coordinates in absolute PDF user
 * space.
 */
export function wireRectToFitR(
  rect: ZoomRect,
  rawDims: PdfRawDims,
): { left: number; bottom: number; right: number; top: number } {
  const { pageWidth, pageHeight, pageX, pageY } = rawDims;
  return {
    left: pageX + rect.left * pageWidth,
    right: pageX + rect.right * pageWidth,
    // Wire Y is top-down; PDF Y is bottom-up, so the wire's bottom edge (large)
    // becomes the low PDF Y and the wire's top edge (small) the high PDF Y.
    bottom: pageY + (1 - rect.bottom) * pageHeight,
    top: pageY + (1 - rect.top) * pageHeight,
  };
}

export interface WirePoint {
  x: number;
  y: number;
}

/**
 * Inverse of the receiver-side laserpointer projection: given a cursor in
 * client coordinates and the page DIV's bounding rect, return the wire point
 * (normalized [0,1] in unrotated PDF logical space, top-down) — or `null` if
 * the cursor is outside the page. Uses `viewport.convertToPdfPoint` so all
 * rotations (0/90/180/270) collapse to a single code path.
 */
export function pointFromViewport(
  pageRect: ZoomBoundingRect,
  clientX: number,
  clientY: number,
  viewport: ZoomViewport,
): WirePoint | null {
  if (viewport.width <= 0 || viewport.height <= 0) return null;
  const xDiv = clientX - pageRect.left;
  const yDiv = clientY - pageRect.top;
  if (xDiv < 0 || yDiv < 0 || xDiv > viewport.width || yDiv > viewport.height) {
    return null;
  }
  const [pdfX, pdfY] = viewport.convertToPdfPoint(xDiv, yDiv);
  // viewBox is [xMin, yMin, xMax, yMax] — see visibleRectFromViewport.
  const [vbX0, vbY0, vbX1, vbY1] = viewport.viewBox;
  const vbW = vbX1 - vbX0;
  const vbH = vbY1 - vbY0;
  if (vbW <= 0 || vbH <= 0) return null;
  return {
    x: (pdfX - vbX0) / vbW,
    y: 1 - (pdfY - vbY0) / vbH,
  };
}
